const GPT_SLOT_ID_SELECTOR = '[id^="div-gpt-ad-"]';
const REFRESH_DELAY_MS = 50;

let refreshTimer = null;
let hasCompletedInitialLoad = false;
let pendingForce = false;

/**
 * Wrap a CMS GPT init script so slot definitions can be re-run on SPA navigation.
 * destroySlots runs first so defineSlot can reuse the same div ids.
 * No-ops when the script is not a slot-definition snippet, or is already wrapped.
 * @param {string} text
 * @return {string}
 */
export function wrapGptDefineSlotsScript(text) {
  if (!text || !/googletag\s*\.\s*defineSlot/.test(text)) {
    return text;
  }
  if (/window\.defineGptSlots\s*=/.test(text)) {
    return text;
  }

  return [
    'window.defineGptSlots = function defineGptSlots() {',
    '  window.googletag = window.googletag || {cmd: []};',
    '  googletag.cmd.push(function() {',
    '    if (typeof googletag.destroySlots === "function") {',
    '      googletag.destroySlots();',
    '    }',
    '  });',
    text,
    '};',
    'window.defineGptSlots();',
  ].join('\n');
}

function collectGptSlotIds(root = document) {
  return [...root.querySelectorAll(GPT_SLOT_ID_SELECTOR)]
      .map((el) => el.id)
      .filter(Boolean);
}

/**
 * Re-define GPT slots from the current URL (when defineGptSlots exists) and
 * display every ad div currently in the document.
 * Safe to call when googletag is missing (no-op).
 */
export function refreshGptAds() {
  const googletag = window.googletag;
  if (!googletag?.cmd) return;

  const canRedefine = typeof window.defineGptSlots === 'function';
  if (canRedefine) {
    window.defineGptSlots();
  }

  googletag.cmd.push(() => {
    if (window.Page_SECTION === null) return;

    const ids = collectGptSlotIds();
    if (!ids.length) return;

    if (canRedefine) {
      ids.forEach((id) => {
        if (document.getElementById(id)) googletag.display(id);
      });
      return;
    }

    const slots = typeof googletag.pubads === 'function' ?
      (googletag.pubads().getSlots?.() || []) :
      [];
    const slotsById = new Map(
        slots.map((slot) => [slot.getSlotElementId?.(), slot]),
    );

    const toRefresh = [];
    ids.forEach((id) => {
      if (!document.getElementById(id)) return;
      const existing = slotsById.get(id);
      if (existing && typeof googletag.pubads === 'function') {
        toRefresh.push(existing);
        return;
      }
      googletag.display(id);
    });

    if (toRefresh.length && typeof googletag.pubads === 'function') {
      googletag.pubads().refresh(toRefresh);
    }
  });
}

/**
 * Coalesce GPT refreshes from multiple StaticBlockComponent instances / route hooks.
 * Skips the first call so the original head + slot HTML can display ads on cold load.
 * @param {{force?: boolean}} [options]
 */
export function scheduleGptAdsRefresh({force = false} = {}) {
  pendingForce = pendingForce || force;
  if (refreshTimer) {
    clearTimeout(refreshTimer);
  }

  refreshTimer = setTimeout(() => {
    refreshTimer = null;
    const forceRefresh = pendingForce;
    pendingForce = false;

    if (!hasCompletedInitialLoad && !forceRefresh) {
      hasCompletedInitialLoad = true;
      return;
    }

    hasCompletedInitialLoad = true;
    refreshGptAds();
  }, REFRESH_DELAY_MS);
}

/** @param {import('vue-router').Route} from */
export function onSpaNavigation(from) {
  if (!from?.matched?.length) return;
  scheduleGptAdsRefresh({force: true});
}

export function resetGptAdsState() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  hasCompletedInitialLoad = false;
  pendingForce = false;
}
